"use client";

import { useRef, useState, useEffect } from "react";
import { UserRound, LogOut, Settings, Sliders, LineChart } from "lucide-react";
import Image from "next/image";
import { useRouter, Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { useCurrentUser } from "@/hooks/useCurrentUser";

export function UserMenu() {
  const [open, setOpen] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const t = useTranslations("Navigation");
  const { data } = useCurrentUser();
  const isAdmin = data?.role === "ADMIN";
  const showAvatar = !!data?.avatar_url && !avatarError;

  useEffect(() => {
    setAvatarError(false);
  }, [data?.avatar_url]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    router.push("/login");
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="h-8 w-8 rounded-full bg-surface-2 flex items-center justify-center ring-1 ring-border hover:ring-accent transition-all ml-1 overflow-hidden"
        aria-haspopup="true"
        aria-expanded={open}
      >
        {showAvatar ? (
          <Image
            src={data.avatar_url!}
            alt={data.email}
            width={32}
            height={32}
            className="rounded-full object-cover"
            onError={() => setAvatarError(true)}
            unoptimized
          />
        ) : (
          <UserRound className="h-4 w-4 text-muted" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-56 bg-surface border border-border rounded-lg shadow-xl overflow-hidden z-50">
          <Link
            href="/profile"
            onClick={() => setOpen(false)}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-text hover:bg-surface-2 transition-colors border-b border-border"
          >
            <UserRound className="h-4 w-4 text-muted" />
            {t("profile")}
          </Link>
          <Link
            href="/preferences"
            onClick={() => setOpen(false)}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-text hover:bg-surface-2 transition-colors border-b border-border"
          >
            <Sliders className="h-4 w-4 text-muted" />
            {t("preferences")}
          </Link>
          <Link
            href="/market"
            onClick={() => setOpen(false)}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-text hover:bg-surface-2 transition-colors border-b border-border"
          >
            <LineChart className="h-4 w-4 text-muted" />
            {t("customizeMarket")}
          </Link>
          {isAdmin && (
            <Link
              href="/admin"
              onClick={() => setOpen(false)}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-text hover:bg-surface-2 transition-colors"
            >
              <Settings className="h-4 w-4 text-muted" />
              {t("admin")}
            </Link>
          )}
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-text hover:bg-surface-2 transition-colors"
          >
            <LogOut className="h-4 w-4 text-muted" />
            {t("logout")}
          </button>
        </div>
      )}
    </div>
  );
}
